import { VerificationDialogData } from './verification-dialog.component';
import { VerificationAdapterService } from './verification-adapter.service';

/**
 * Helper class to create dialog configurations for different verification types
 */
export class VerificationDialogHelper {

  /**
   * Creates configuration for LoA verification dialog
   */
  static createConsentVerificationConfig(
    requestId: string,
    meterCount: number,
    adapterService: VerificationAdapterService
  ): VerificationDialogData {
    return {
      pollFunction: adapterService.createConsentPollingFunction(requestId),
      title: 'Consent validation is in Progress',
      message: `We are validating your request to add consent for ${meterCount} meters.`,
      checkingMessage: 'Starting verification process...',
      successMessage: 'Consent verification successful!',
      errorMessage: 'Consent verification failed. Please try again or try with a different consent method.',
      pollingInterval: 3000,
      maxAttempts: 40, // 2 minutes
      showRetryButton: true,
      waitingMessage: meterCount > 100 
        ? 'This process may take upto 3 minutes. We appreciate your patience and please do not close this window.'
        : 'This process may take upto a minute. We appreciate your patience and please do not close this window.'
    };
  }

  /**
   * Creates configuration for Digital Signature verification dialog
   */
  static createDigitalSignatureVerificationConfig(
    consentId: string,
    meterCount: number,
    adapterService: VerificationAdapterService,
    windowToClose?: Window | null
  ): VerificationDialogData {
    return {
      pollFunction: adapterService.createDigitalSignaturePollingFunction(consentId),
      title: 'Digital Signature Verification in Progress',
      message: `We are verifying your digital signature for ${meterCount} meters. Please complete the signing process in the opened tab.`,
      checkingMessage: 'Checking digital signature status...',
      successMessage: 'Digital signature verified successfully! You can now close the signing tab.',
      errorMessage: 'Digital signature verification failed.',
      pollingInterval: 3000,
      maxAttempts: 40, // 2 minutes
      showRetryButton: true,
      windowToClose: windowToClose,
      waitingMessage: meterCount > 100 
        ? 'This process may take upto 3 minutes. We appreciate your patience and please do not close this window.'
        : 'This process may take upto a minute. We appreciate your patience and please do not close this window.'
    };
  }

  static createSmartMeterVerificationConfig(
    requestId: string,
    meterCount: number,
    adapterService: VerificationAdapterService
  ): VerificationDialogData {
    return {
      pollFunction: adapterService.createConsentPollingFunction(requestId),
      title: 'Smart Meter Verification in Progress',
      message: `We are verifying your smart meter request for ${meterCount} meters.`,
      checkingMessage: 'Starting verification process...',
      successMessage: 'Smart meter verification successful!',
      errorMessage: 'Verification failed. Please check your input and try again. You may also try with a different method of providing consent or a different granularity.',
      pollingInterval: 3000,
      maxAttempts: 40, // 2 minutes
      showRetryButton: true,
      waitingMessage: meterCount > 100 
        ? 'This process may take upto 3 minutes. We appreciate your patience and please do not close this window.'
        : 'This process may take upto a minute. We appreciate your patience and please do not close this window.'
    };
  }

  /**
   * Creates a generic verification config
   */
  static createGenericVerificationConfig(
    pollFunction: () => any,
    title: string,
    message: string,
    successMessage: string,
    errorMessage: string,
    options?: {
      checkingMessage?: string;
      pollingInterval?: number;
      maxAttempts?: number;
      showRetryButton?: boolean;
      windowToClose?: Window | null;
      waitingMessage?: string;
    }
  ): VerificationDialogData {
    return {
      pollFunction,
      title,
      message,
      successMessage,
      errorMessage,
      checkingMessage: options?.checkingMessage || 'Starting verification process...',
      pollingInterval: options?.pollingInterval || 3000,
      maxAttempts: options?.maxAttempts || 60,
      showRetryButton: options?.showRetryButton !== false,
      windowToClose: options?.windowToClose,
      waitingMessage: options?.waitingMessage
    };
  }
}
